import { useEffect, useState } from 'react';
import {
  Sparkles,
  Mail,
  Lightbulb,
  Share2,
  Facebook,
  Instagram,
  Youtube,
  X,
  Loader2,
  CheckCircle2,
  Send,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { submitForm } from '@/lib/submission';
import { toast } from 'sonner';
import { ShareDialog } from '@/components/share/ShareDialog';
import { buildSocialUrl } from '@/lib/social-links';
import { trackFollowClick } from '@/lib/follow-prompt';

const STORAGE_KEY = 'mnel.welcome-strip.dismissed-v1';

export function WelcomeStrip() {
  const [visible, setVisible] = useState(false);

  // Newsletter state
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // Suggestion state
  const [suggestion, setSuggestion] = useState('');
  const [suggestionEmail, setSuggestionEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY) !== '1') setVisible(true);
    } catch {
      setVisible(true);
    }
  }, []);

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* noop */ }
  };

  const submitNewsletter = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error('Ugyldig email');
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke('newsletter-subscribe', {
        body: { email: trimmed },
      });
      if (error) throw error;
      setDone(true);
      toast.success('Tjek din indbakke for at bekræfte tilmeldingen');
    } catch (err: any) {
      toast.error(err?.message || 'Kunne ikke tilmelde – prøv igen');
    } finally {
      setLoading(false);
    }
  };

  const submitSuggestion = async (e: React.FormEvent) => {
    e.preventDefault();
    const msg = suggestion.trim();
    if (!msg) {
      toast.error('Skriv et forslag først');
      return;
    }
    setSending(true);
    const payload = {
      type: 'feedback' as const,
      message: msg,
      email: suggestionEmail.trim() || null,
      page_url: typeof window !== 'undefined' ? window.location.href : null,
    };
    const res = await submitForm({
      formName: 'welcome_strip_suggestion',
      table: 'feedback',
      payload,
      email: payload.email,
      meta: { source: 'welcome-strip' },
      perform: () => supabase.from('feedback').insert(payload),
    });
    if (res.ok) {
      setSent(true);
      toast.success('Tak for dit forslag!');
    } else {
      toast.error(res.userMessage ?? 'Kunne ikke sende – prøv igen');
    }
    setSending(false);
  };

  if (!visible) return null;

  return (
    <>
      <section
        aria-label="Velkommen til Min Nye Elbil"
        className="relative bg-accent/10 border-b border-accent/30"
      >
        <div className="container mx-auto px-4 py-3 sm:py-4 pr-10 sm:pr-12">
          {/* Header */}
          <div className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-accent flex-shrink-0 mt-0.5" />
            <p className="text-sm sm:text-base text-foreground leading-snug">
              <span className="font-semibold">Min Nye Elbil er helt ny</span>
              <span className="text-muted-foreground"> — hjælp os i gang.</span>
            </p>
          </div>

          {/* Two halves */}
          <div className="mt-3 grid gap-4 md:grid-cols-2 md:gap-6 md:divide-x md:divide-accent/30">
            {/* Left: Newsletter + share */}
            <div className="md:pr-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Få nyhedsbrevet
              </p>
              {done ? (
                <div className="flex items-center gap-2 text-sm text-foreground">
                  <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0" />
                  <span>Tak! Tjek din indbakke for at bekræfte.</span>
                </div>
              ) : (
                <form onSubmit={submitNewsletter} className="flex items-center gap-2">
                  <div className="relative flex-1 min-w-0">
                    <Mail className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <Input
                      type="email"
                      inputMode="email"
                      placeholder="din@email.dk"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={loading}
                      className="h-10 pl-9 bg-background"
                      aria-label="Email-adresse til nyhedsbrev"
                    />
                  </div>
                  <Button
                    type="submit"
                    variant="accent"
                    disabled={loading}
                    className="h-10 whitespace-nowrap flex-shrink-0"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Tilmeld'}
                  </Button>
                </form>
              )}

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 h-9"
                  onClick={() => setShareOpen(true)}
                >
                  <Share2 className="w-3.5 h-3.5" />
                  Del siden
                </Button>
                <span aria-hidden="true" className="mx-1 h-5 w-px bg-border" />
                <a
                  href={buildSocialUrl('facebook', 'welcome-strip')}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Følg Min Nye Elbil på Facebook"
                  onClick={() => trackFollowClick('facebook', 'welcome-strip')}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-md text-foreground hover:bg-accent/20 transition-colors"
                >
                  <Facebook className="w-4 h-4" />
                </a>
                <a
                  href={buildSocialUrl('instagram', 'welcome-strip')}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Følg Min Nye Elbil på Instagram"
                  onClick={() => trackFollowClick('instagram', 'welcome-strip')}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-md text-foreground hover:bg-accent/20 transition-colors"
                >
                  <Instagram className="w-4 h-4" />
                </a>
                <a
                  href={buildSocialUrl('youtube', 'welcome-strip')}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Følg Min Nye Elbil på YouTube"
                  onClick={() => trackFollowClick('youtube', 'welcome-strip')}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-md text-foreground hover:bg-accent/20 transition-colors"
                >
                  <Youtube className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Right: Suggestion form */}
            <div className="md:pl-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
                <Lightbulb className="w-3.5 h-3.5 text-accent" />
                Hvad mangler siden?
              </p>
              {sent ? (
                <div className="flex items-center gap-2 text-sm text-foreground">
                  <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0" />
                  <span>Tak! Vi læser alle forslag.</span>
                </div>
              ) : (
                <form onSubmit={submitSuggestion} className="space-y-2">
                  <Textarea
                    placeholder="Fortæl os hvad siden skal kunne, eller hvad du savner..."
                    value={suggestion}
                    onChange={(e) => setSuggestion(e.target.value)}
                    disabled={sending}
                    rows={2}
                    className="bg-background resize-none"
                    aria-label="Dit forslag"
                  />
                  <div className="flex items-center gap-2">
                    <Input
                      type="email"
                      inputMode="email"
                      placeholder="Email (valgfrit)"
                      value={suggestionEmail}
                      onChange={(e) => setSuggestionEmail(e.target.value)}
                      disabled={sending}
                      className="h-10 bg-background flex-1 min-w-0"
                      aria-label="Email (valgfrit)"
                    />
                    <Button
                      type="submit"
                      variant="accent"
                      disabled={sending}
                      className="h-10 whitespace-nowrap flex-shrink-0 gap-1.5"
                    >
                      {sending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Send className="w-3.5 h-3.5" />
                          Send forslag
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={dismiss}
            aria-label="Luk velkomstbesked"
            className="absolute top-2 right-2 inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-accent/20 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </section>

      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        title={typeof document !== 'undefined' ? document.title : 'Min Nye Elbil'}
        url={typeof window !== 'undefined' ? window.location.href : ''}
        context="generic"
      />
    </>
  );
}
